import { FunctionComponent, useEffect, useState } from 'react';
import { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@material-ui/core';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';

import Marketplace from '@spike/marketplace-model';
import { NewBookingAlert } from '@spike/new-booking-model';
import { fetchAlertsThunk } from '@spike/new-bookings-action';
import { RootState } from 'store';
import { useApiClientWrapper, useMarketplace, useTimeZone } from 'hooks';
import { getDefaultTimeRangeFromOpening, type TimeRange } from 'utils/timeRangeUtils';

import MembershipsTimeSection from './UI/MembershipsTimeSection/MembershipsTimeSection';
import MembershipsSelector, { MembershipsSelection } from './UI/MembershipsSelector/MembershipsSelector';
import { AlertNewBooking } from '../UI/AlertNewBooking';
import { RecentlyBooked } from '../UI/RecentlyBooked';
import { AppointmentHistoryDrawer } from '../UI/AppointmentHistoryDrawer';
import OrderDetail from 'components/OrderDetail';
import { AppointmentListItem } from '@spike/appointments-list-model';
import { versionPetAndServices } from 'model/NewMultipleBooking';

import styles from './MembershipsComponent.module.scss';

function buildMembershipTimeRange(bookingDate: Moment, marketplace: Marketplace | undefined, tz: string): TimeRange {
    const roundUpToNext15Minutes = (value: Moment): Moment => {
        const rounded = value.clone().second(0).millisecond(0);
        const remainder = rounded.minutes() % 15;
        if (remainder !== 0) {
            rounded.add(15 - remainder, 'minutes');
        }
        return rounded;
    };

    const isMidnight = (v: Moment) => v.hours() === 0 && v.minutes() === 0;

    const base = bookingDate?.isValid() ? bookingDate.clone().tz(tz) : moment().tz(tz);

    if (!isMidnight(base)) {
        const from = roundUpToNext15Minutes(base);
        return { from, to: from.clone().add(15, 'minutes') };
    }

    if (marketplace?.schedule?.week) {
        const dateInMarketTz = base.clone().tz(marketplace.schedule.timeZone, true);
        const dayOfWeek = dateInMarketTz.format('dddd').toLowerCase();
        const businessHours = marketplace.schedule.week[dayOfWeek as keyof typeof marketplace.schedule.week];
        const [hour, minute] = businessHours?.open?.split(':').map(Number) || [8, 0];
        const opening = dateInMarketTz.clone().startOf('day').hour(hour).minute(minute).second(0).millisecond(0);
        return getDefaultTimeRangeFromOpening(opening, tz);
    }

    const day = base.clone().startOf('day');
    const fallbackFrom = day.clone().hour(8).minute(0).second(0).millisecond(0);
    return { from: fallbackFrom, to: fallbackFrom.clone().add(2, 'hours') };
}

interface MembershipsProps {
    clientId: number;
    petId: number;
    date: Moment;
    onChangeDate: (value: Moment) => void;
    versionPetAndServices?: versionPetAndServices;
    isEvaluation?: boolean;
    onViewAllRecentBookings?: () => void;
    onMembershipServiceSelect?: (selection: MembershipsSelection) => void;
    membershipPetServicesLoading?: boolean;
}

export const MembershipsComponent: FunctionComponent<MembershipsProps> = ({
    clientId,
    petId,
    date,
    onChangeDate,
    versionPetAndServices: version,
    isEvaluation,
    onViewAllRecentBookings,
    onMembershipServiceSelect,
    membershipPetServicesLoading
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const timeZone = useTimeZone();
    const alerts = useSelector<RootState, Array<NewBookingAlert> | undefined>(state => state.newBookings.alerts);

    const [showAppointmentHistoryDrawer, setShowAppointmentHistoryDrawer] = useState(false);
    const [recentlyBookedRefreshKey, setRecentlyBookedRefreshKey] = useState(0);
    const [showOrderDetail, setShowOrderDetail] = useState<{ orderId: number; bookingId: number } | null>(null);

    const [timeRange, setTimeRange] = useState<TimeRange>(() => buildMembershipTimeRange(date, marketplace, timeZone));

    const dateKey = date?.isValid() ? date.clone().tz(timeZone).format('YYYY-MM-DD') : '';

    useEffect(() => {
        if (!date?.isValid()) return;
        const next = buildMembershipTimeRange(date, marketplace, timeZone);
        setTimeRange(next);
        onChangeDate(next.from.clone());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateKey, timeZone]);

    const handleTimeRangeChange = (value: { from: Moment; to: Moment }) => {
        setTimeRange({ from: value.from, to: value.to });
        onChangeDate(value.from.clone());
    };

    useEffect(() => {
        if (!timeRange?.from?.isValid() || !clientId || !petId) return;
        dispatch(
            fetchAlertsThunk(
                apiClientWrapper,
                clientId,
                petId,
                timeRange.from.clone().tz('utc'),
                false,
                undefined,
                undefined,
                undefined
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeRange.from, clientId, petId]);

    const showRecentlyBooked = version !== versionPetAndServices.edit && !isEvaluation;

    const hasDateTimeAlert = Boolean(alerts?.some(alert => alert.alertType === 'date_time'));

    return (
        <div className={styles.walkingContainer}>
            <div className={styles.configContainer}>
                <div className={styles.section}>
                    <div className={styles.dateTimeBlock}>
                        <MembershipsTimeSection
                            dateSelectorId="memberships-appointment-date"
                            from={timeRange.from}
                            to={timeRange.to}
                            onChange={handleTimeRangeChange}
                        />
                    </div>

                    {hasDateTimeAlert && (
                        <Box
                            className={styles.alertBox}
                            id="booking_div_date_time_alert"
                        >
                            <AlertNewBooking
                                className={styles.alert}
                                bWidth="2px"
                                icon={faExclamationTriangle}
                                iconSize="lg"
                                iconColor="#EAB464"
                                bgColor="#F8F5F1"
                                bdColor="#BCB8AE"
                            >
                                <Typography className={styles.alertText}>Date/Time outside business hours</Typography>
                            </AlertNewBooking>
                        </Box>
                    )}

                    {showRecentlyBooked && (
                        <div className={styles.section}>
                            <div className={styles.recentlyBookedSection}>
                                <RecentlyBooked
                                    clientId={clientId}
                                    petId={petId}
                                    refreshTrigger={recentlyBookedRefreshKey}
                                    onViewAll={() => {
                                        setShowAppointmentHistoryDrawer(true);
                                        onViewAllRecentBookings?.();
                                    }}
                                    onAppointmentClick={(appointment: AppointmentListItem) => {
                                        if (appointment.orderId != null && appointment.bookingId != null) {
                                            setShowOrderDetail({
                                                orderId: appointment.orderId,
                                                bookingId: appointment.bookingId
                                            });
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className={styles.section}>
                        <div className={styles.membershipsSelectorSection}>
                            <MembershipsSelector
                                clientId={clientId}
                                petId={petId}
                                date={timeRange.from}
                                onSelect={onMembershipServiceSelect}
                                petServicesLoading={membershipPetServicesLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <AppointmentHistoryDrawer
                open={showAppointmentHistoryDrawer}
                onBack={() => {
                    setShowAppointmentHistoryDrawer(false);
                    setRecentlyBookedRefreshKey(k => k + 1);
                }}
                customerId={clientId}
                petId={petId}
                onAppointmentClick={(appointment: AppointmentListItem) => {
                    if (appointment.orderId != null && appointment.bookingId != null) {
                        setShowOrderDetail({
                            orderId: appointment.orderId,
                            bookingId: appointment.bookingId
                        });
                    }
                }}
            />
            {showOrderDetail && (
                <OrderDetail
                    orderId={showOrderDetail.orderId}
                    bookingId={showOrderDetail.bookingId}
                    readOnly={true}
                    onClose={() => setShowOrderDetail(null)}
                    zIndex={10100}
                />
            )}
        </div>
    );
};

export default MembershipsComponent;
